import { API_BASE } from './client';

/** Minimal EventSource surface, so mock mode and tests can plug their own. */
export interface StreamSource {
  addEventListener(type: string, listener: (ev: MessageEvent) => void): void;
  close(): void;
  onerror: ((ev: Event) => void) | null;
  onopen: ((ev: Event) => void) | null;
}

export type StreamFactory = (url: string) => StreamSource;

let factory: StreamFactory = (url) => new EventSource(url, { withCredentials: true }) as unknown as StreamSource;

export function setStreamFactory(f: StreamFactory): void {
  factory = f;
}

export interface StreamHandlers {
  [event: string]: (data: unknown) => void;
}

export interface StreamOptions {
  onOpen?: () => void;
  onError?: () => void;
}

/**
 * Subscribe to an SSE endpoint under /api. Each named event's data is JSON-parsed.
 * Returns an unsubscribe function. The browser reconnects by itself on transient errors;
 * a `done` event closes the stream for good.
 */
export function openStream(path: string, on: StreamHandlers, opts: StreamOptions = {}): () => void {
  const src = factory(API_BASE + path);
  let closed = false;
  for (const [event, fn] of Object.entries(on)) {
    src.addEventListener(event, (ev: MessageEvent) => {
      if (closed) return;
      let data: unknown = ev.data;
      try {
        data = JSON.parse(ev.data as string);
      } catch {
        /* keep raw */
      }
      fn(data);
      if (event === 'done') {
        closed = true;
        src.close();
      }
    });
  }
  src.onopen = () => opts.onOpen?.();
  src.onerror = () => {
    if (!closed) opts.onError?.();
  };
  return () => {
    closed = true;
    src.close();
  };
}
